import React from 'react';
import { Grid2 } from '@mui/material';
import { MetricCard, SummaryCard, PercentageLineChart, PieChart, CompareInfoCards, ScoreCard, StackedAreaChart, AlertView, DonutChart, BarChart, HorizontalBarChart, LineChart, HeatMap, AreaChart, Histogram, ScatterChart, StackedHistogram, RadarChart } from '../graphs';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  Chat as ChatIcon,
  Phone as PhoneIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
  ReportProblem as ReportProblemIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Sick as SickIcon
} from '@mui/icons-material';
import { Box, Stack } from '@mui/material';

export interface ChartConfig {
  key: string;
  graph: string;
  title: string;
  description: string;
  transform: (data: any, fullData?: any) => any;
  additionalProps?: Record<string, any>;
  wrapper?: string;
  wrapperProps?: Record<string, any>;
  icon?: string;
  gridExist?: boolean;
}

export interface LayoutItem {
  gridSize: { xs: number; md?: number };
  components: ChartConfig[];
  wrapper?: string;
  wrapperProps?: Record<string, any>;
}

export interface DataConfig {
  key: string;
  graph: string;
  transform: (data: any, fullData?: any) => any;
}

export interface StyleConfig {
  key: string;
  title: string;
  description: string;
  additionalProps?: Record<string, any>;
  wrapper?: string;
  wrapperProps?: Record<string, any>;
  gridExist?: boolean;
}

export interface StyleLayoutItem {
  gridSize: { xs: number; md?: number };
  components: StyleConfig[];
  wrapper?: string;
  wrapperProps?: Record<string, any>;
}

interface AnalyticsContentProps {
  dataConfigs: DataConfig[];
  styleLayout: StyleLayoutItem[];
  analyticsData: any;
  onDetailsClick?: (data?: any) => void;
  // onMetricCardClick?: (key: string, data: any) => void;
}

const AnalyticsContent: React.FC<AnalyticsContentProps> = ({ dataConfigs, styleLayout, analyticsData, onDetailsClick }) => {
  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const dataConfigMap = new Map(dataConfigs.map(config => [config.key, config]));

  // Helper function to get title and description from JSON metrics if available
  const getMetricInfo = (key: string) => {
    const metric = analyticsData?.metrics?.find((m: any) => m.metric_key === key);
    return {
      title: metric?.title || null,
      description: metric?.description || null
    };
  };

  const layout: LayoutItem[] = styleLayout.map(styleItem => ({
    gridSize: styleItem.gridSize,
    wrapper: styleItem.wrapper,
    wrapperProps: styleItem.wrapperProps,
    components: styleItem.components.map(styleComp => {
      const dataConfig = dataConfigMap.get(styleComp.key);
      if (!dataConfig) return null;

      // Get title and description from JSON if available, otherwise use style config
      const metricInfo = getMetricInfo(styleComp.key);

      return {
        key: styleComp.key,
        graph: dataConfig.graph,
        title: metricInfo.title || styleComp.title,
        description: metricInfo.description || styleComp.description,
        transform: dataConfig.transform,
        additionalProps: styleComp.additionalProps,
        wrapper: styleComp.wrapper,
        wrapperProps: styleComp.wrapperProps,
        gridExist: styleComp.gridExist
      } as ChartConfig;
    }).filter(Boolean) as ChartConfig[]
  }));

  const handleComponentClick = (data: any) => {
    if (onDetailsClick) {
      onDetailsClick(data);
    }
  };

  const renderComponent = (config: ChartConfig) => {
    const data = getNestedValue(analyticsData, config.key);
    let transformedData = config.transform(data, analyticsData);

    // Normalize transform output so we always get { payload, details }
    let payload: any = null;
    let details: any = null;

    if (transformedData && typeof transformedData === 'object') {
      if ('payload' in transformedData || 'details' in transformedData) {
        payload = transformedData.payload ?? null;
        details = transformedData.details ?? null;
      } else if ('analyticsData' in transformedData || 'info' in transformedData) {
        // fallback for older shape: { analyticsData, info }
        payload = transformedData.analyticsData ?? null;
        details = transformedData.info ?? null;
      } else {
        // if transform returned a plain array/object intended as payload
        payload = transformedData;
        details = null;
      }
    } else {
      payload = transformedData;
      details = null;
    }

    // If the payload contains an icon string, replace with component
    if (payload && typeof payload === 'object' && 'icon' in payload && typeof payload.icon === 'string') {
      const icons = { PeopleIcon, TrendingUpIcon, AccessTimeIcon, ChatIcon, PhoneIcon, SentimentSatisfiedIcon, SentimentDissatisfiedIcon, SentimentVeryDissatisfiedIcon, ReportProblemIcon, EmojiEmotionsIcon, SickIcon };
      const IconComponent = icons[payload.icon as keyof typeof icons];
      if (IconComponent) payload.icon = <IconComponent />;
    }

    const components = { MetricCard, SummaryCard, PercentageLineChart, CompareInfoCards, PieChart, StackedAreaChart, ScoreCard, AlertView, DonutChart, BarChart, HorizontalBarChart, LineChart, HeatMap, AreaChart, Histogram, ScatterChart, StackedHistogram, RadarChart };
    const Component = (components as any)[config.graph];
    if (!Component) return null;
    const element = (
      <Component
        title={config.title}
        description={config.description}
        data={payload}
        details={details}
        gridExist={config.gridExist}
        onDetailsClick={(itemData?: any) => handleComponentClick({ key: config.key, payload, details, itemData })}
        {...config.additionalProps}
      />
    );

    if (config.wrapper) {
      const wrappers = { Box, Stack };
      const Wrapper = wrappers[config.wrapper as keyof typeof wrappers];
      if (Wrapper) {
        return <Wrapper {...config.wrapperProps}>{element}</Wrapper>;
      }
    }

    return element;
  };

  return (
    <Grid2 container spacing={1}>
      {layout.map((layoutItem, index) => (
        <Grid2 key={index} size={layoutItem.gridSize}>
          {layoutItem.wrapper ? (() => {
            const wrappers = { Box, Stack };
            const Wrapper = wrappers[layoutItem.wrapper as keyof typeof wrappers];
            return Wrapper ? (
              <Wrapper {...layoutItem.wrapperProps}>
                {layoutItem.components.map((config, compIndex) => (
                  <React.Fragment key={compIndex}>
                    {renderComponent(config)}
                  </React.Fragment>
                ))}
              </Wrapper>
            ) : (
              layoutItem.components.map((config, compIndex) => (
                <React.Fragment key={compIndex}>
                  {renderComponent(config)}
                </React.Fragment>
              ))
            );
          })() : (
            layoutItem.components.map((config, compIndex) => (
              <React.Fragment key={compIndex}>
                {renderComponent(config)}
              </React.Fragment>
            ))
          )}
        </Grid2>
      ))}
    </Grid2>
  );
};

export default AnalyticsContent;